import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/api';
import { Bell, CheckCheck, MessageSquare, X } from 'lucide-react';

const ICON_MAP = {
  review_reply: MessageSquare,
};

function timeAgo(iso) {
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60)   return 'adesso';
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ore fa`;
  return `${Math.floor(diff / 86400)} giorni fa`;
}

export function NotificationBell() {
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifs(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* silenzioso */ }
  }, []);

  // Carica all'avvio e ogni 30 secondi
  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  // Chiude se click fuori
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = async () => {
    setOpen(o => !o);
    if (!open && unread > 0) {
      // Segna tutte come lette quando apre
      await load();
    }
  };

  const markAllRead = async () => {
    if (unread === 0) return;
    setLoading(true);
    try {
      await api.put('/notifications/read-all');
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const markRead = async (notif) => {
    if (notif.read) return;
    try {
      await api.put(`/notifications/${notif.id}/read`);
      setNotifs(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      setUnread(u => Math.max(0, u - 1));
    } catch { /* ignore */ }
  };

  return (
    <div ref={ref} className="relative" data-testid="notification-bell">
      {/* Campanellino */}
      <button
        onClick={handleOpen}
        data-testid="bell-button"
        className="relative flex items-center justify-center w-8 h-8 rounded-full transition-all hover:bg-white/5"
        style={{ color: unread > 0 ? '#c9a84c' : 'var(--lux-text-muted-3)' }}
        title={unread > 0 ? `${unread} notifiche non lette` : 'Nessuna notifica'}
      >
        <Bell size={15} strokeWidth={unread > 0 ? 2 : 1.5} />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center leading-none"
            style={{ background: '#ef4444', color: '#fff' }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          data-testid="notif-dropdown"
          className="absolute right-0 top-10 z-50 w-80 rounded-lg shadow-2xl overflow-hidden"
          style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-2)' }}
        >
          {/* Header dropdown */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid var(--lux-border-1)' }}
          >
            <p className="text-xs font-semibold text-white">Notifiche</p>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  disabled={loading}
                  data-testid="mark-all-read-btn"
                  className="flex items-center gap-1 text-[10px] transition-colors hover:opacity-80"
                  style={{ color: '#c9a84c' }}
                  title="Segna tutte come lette"
                >
                  <CheckCheck size={12} /> Tutte lette
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="transition-colors hover:opacity-70"
                style={{ color: 'var(--lux-text-muted-3)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={13} />
              </button>
            </div>
          </div>

          {/* Lista notifiche */}
          <div className="max-h-80 overflow-y-auto">
            {notifs.length === 0 ? (
              <div className="py-10 text-center">
                <Bell size={24} className="mx-auto mb-2 opacity-20" style={{ color: 'var(--lux-text-muted)' }} />
                <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>Nessuna notifica</p>
              </div>
            ) : (
              notifs.map((n) => {
                const Icon = ICON_MAP[n.type] || Bell;
                return (
                  <div
                    key={n.id}
                    onClick={() => markRead(n)}
                    data-testid={`notif-item-${n.id}`}
                    className="flex gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-white/3"
                    style={{
                      borderBottom: '1px solid var(--lux-border-1)',
                      background: n.read ? 'transparent' : 'rgba(201,168,76,0.04)',
                    }}
                  >
                    {/* Icona */}
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{
                        background: n.read ? 'var(--lux-surface-3)' : 'rgba(201,168,76,0.15)',
                        border: n.read ? '1px solid var(--lux-border-2)' : '1px solid rgba(201,168,76,0.3)',
                      }}
                    >
                      <Icon size={12} style={{ color: n.read ? 'var(--lux-text-muted-3)' : '#c9a84c' }} />
                    </div>

                    {/* Testo */}
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-xs font-medium mb-0.5 leading-snug"
                        style={{ color: n.read ? 'var(--lux-text-muted)' : 'var(--lux-text)' }}
                      >
                        {n.title}
                      </p>
                      <p className="text-[11px] leading-snug" style={{ color: 'var(--lux-text-muted-3)' }}>
                        {n.body}
                      </p>
                      <p className="text-[9px] mt-1.5" style={{ color: 'var(--lux-text-muted-3)', opacity: 0.6 }}>
                        {timeAgo(n.created_at)}
                      </p>
                    </div>

                    {/* Punto non letto */}
                    {!n.read && (
                      <div className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] mt-1.5 flex-shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
